import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fileUrl, uploadSiteImage } from "@/lib/site";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CircleNotch, ArrowSquareOut, Plus, X, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50);

function Gallery({ ids, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    const out = [...ids];
    for (const f of files) {
      try { out.push(await uploadSiteImage(f)); } catch (err) { toast.error(apiError(err, "Falha ao carregar")); }
    }
    onChange(out.slice(0, 30));
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6" data-testid="site-gallery">
      {ids.map((id, i) => (
        <div key={id} className="relative aspect-square overflow-hidden rounded-lg bg-muted" data-testid={`gallery-item-${i}`}>
          <img src={fileUrl(id)} alt="" className="h-full w-full object-cover" loading="lazy" />
          <button type="button" onClick={() => onChange(ids.filter((x) => x !== id))}
            className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white" data-testid={`gallery-remove-${i}`}>
            <X size={12} />
          </button>
        </div>
      ))}
      {ids.length < 30 && (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="gallery-add-btn"
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:bg-accent">
          {busy ? <CircleNotch size={20} className="animate-spin" /> : <Plus size={20} />} Adicionar
        </button>
      )}
      <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={add} />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;
}

const THEMES = [
  ["classico", "Clássico", "Tons quentes, terracota", ["#FBF7F0", "#2B2118", "#B4532A"]],
  ["verde", "Verde Atlântico", "Verde-petróleo e verde-menta", ["#F3F7F5", "#0E4B47", "#3CCB9A"]],
];

function ThemePicker({ value, onChange }) {
  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-5" data-testid="site-theme-picker">
      <h2 className="font-display text-lg font-bold">Estilo do site</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {THEMES.map(([k, name, sub, sw]) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`site-theme-${k}`} aria-pressed={value === k}
            className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-colors ${value === k ? "border-primary bg-accent ring-2 ring-primary/30" : "border-border hover:bg-secondary"}`}>
            <span className="flex shrink-0 overflow-hidden rounded-lg border border-border">
              {sw.map((c) => <span key={c} className="h-10 w-6" style={{ background: c }} />)}
            </span>
            <span><span className="block font-semibold">{name}</span><span className="text-xs text-muted-foreground">{sub}</span></span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function SiteSettings() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => {
      setForm({ ...data, slug: data.slug || slugify(data.name || "") });
      setSaved(data.exists ? data.slug : null);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!form) return <div className="flex h-60 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const publicUrl = saved ? `${window.location.origin}/s/${saved}` : null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/site/settings", form);
      setForm(data);
      setSaved(data.slug);
      toast.success("Site guardado");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 pb-8" data-testid="site-settings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Site público</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">O meu site</h1>
        </div>
        <div className="flex gap-2">
          {publicUrl && (
            <Button variant="outline" className="rounded-full" asChild data-testid="open-public-site-btn">
              <a href={publicUrl} target="_blank" rel="noreferrer"><ArrowSquareOut size={16} className="mr-1.5" /> Ver site</a>
            </Button>
          )}
          <Button className="rounded-full" onClick={save} disabled={busy} data-testid="save-site-btn">
            {busy ? <CircleNotch className="animate-spin" /> : "Guardar"}
          </Button>
        </div>
      </div>

      {publicUrl && (
        <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-accent px-4 py-3 text-sm" data-testid="public-url-box">
          <span className="truncate font-medium text-accent-foreground">{publicUrl}</span>
          <Button size="icon" variant="ghost" className="ml-auto h-8 w-8" data-testid="copy-public-url-btn"
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); }}><Copy size={16} /></Button>
        </div>
      )}

      <ThemePicker value={form.theme || "classico"} onChange={(theme) => setForm({ ...form, theme })} />

      <section className="grid gap-5 rounded-xl border border-border bg-card p-5 lg:grid-cols-[200px_1fr]">
        <Field label="Logótipo"><ImageUpload value={form.logo_id} onChange={(v) => setForm({ ...form, logo_id: v })} testid="site-logo" /></Field>
        <Field label="Foto de capa" className="max-w-3xl">
          <ImageUpload value={form.cover_id} onChange={(v) => setForm({ ...form, cover_id: v })} testid="site-cover"
            aspect="aspect-[16/7]" label="Carregar foto de capa" />
        </Field>
      </section>

      <section className="grid gap-4 rounded-xl border border-border bg-card p-5 md:grid-cols-2">
        <Field label="Nome *"><Input value={form.name} onChange={set("name")} data-testid="site-name" /></Field>
        <Field label="Endereço (slug) *">
          <div className="flex items-center rounded-md border border-input bg-background pl-3 text-sm">
            <span className="text-muted-foreground">/s/</span>
            <input className="h-9 flex-1 bg-transparent px-1 outline-none" value={form.slug} data-testid="site-slug"
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 50) })} />
          </div>
        </Field>
        <Field label="Descrição" className="md:col-span-2">
          <Textarea rows={4} value={form.description} onChange={set("description")} data-testid="site-description" />
        </Field>
        <Field label="Morada"><Input value={form.address} onChange={set("address")} data-testid="site-address" /></Field>
        <Field label="Horário"><Textarea rows={2} placeholder="Seg–Sáb 12h–23h" value={form.hours} onChange={set("hours")} data-testid="site-hours" /></Field>
        <Field label="Telefone"><Input value={form.phone} onChange={set("phone")} placeholder="+238 ..." data-testid="site-phone" /></Field>
        <Field label="WhatsApp"><Input value={form.whatsapp} onChange={set("whatsapp")} placeholder="+238 ..." data-testid="site-whatsapp" /></Field>
        <Field label="Instagram"><Input value={form.instagram} onChange={set("instagram")} placeholder="@aminhaempresa" data-testid="site-instagram" /></Field>
        <div className="flex flex-col justify-end gap-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} data-testid="site-published"
            onCheckedChange={(v) => setForm({ ...form, published: v })} /> Site publicado</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.reservations_enabled} data-testid="site-reservations-enabled"
            onCheckedChange={(v) => setForm({ ...form, reservations_enabled: v })} /> Aceitar reservas online</label>
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold">Galeria</h2>
          <span className="text-xs text-muted-foreground">{form.gallery.length}/30</span></div>
        <Gallery ids={form.gallery} onChange={(g) => setForm({ ...form, gallery: g })} />
      </section>
    </div>
  );
}
